import { Utils } from 'versafleet-core';
import * as Sentry from '@sentry/browser';
import task from './task';

const {
  request, requestBlob, convertDataToParams, requestCustomBody,
} = Utils.RequestHelper;

const baseFilter = {
  per_page: 20,
  sort_by: 'id',
  order_by: 'desc',
};

function resetImportError() {
  return {
    type: 'RESET_JOB_IMPORT_ERROR',
  };
}

function receiveJob(job) {
  return {
    type: 'RECEIVE_JOB',
    job,
  };
}

function receiveJobs(result, timestamp) {
  return {
    type: 'RECEIVE_JOBS',
    jobs: result.jobs,
    meta: result.meta,
    timestamp,
  };
}

function receiveArchivedJobs(result, timestamp) {
  return {
    type: 'RECEIVE_ARCHIVED_JOBS',
    jobs: result.jobs,
    meta: result.meta,
    timestamp,
  };
}

function receiveJobUpdateSuccessful(job) {
  return {
    type: 'RECEIVE_JOB_UPDATE_SUCCESSFUL',
    job,
  };
}

function receiveJobExportSuccessful() {
  return {
    type: 'RECEIVE_JOB_EXPORT_SUCCESSFUL',
  };
}

function receiveExportTaskCountSuccessful(taskCount) {
  return {
    type: 'RECEIVE_EXPORT_TASK_COUNT_SUCCESSFUL',
    taskCount,
  };
}

function receiveResetJobExportTaskCount() {
  return {
    type: 'RECEIVE_RESET_JOB_EXPORT_TASK_COUNT',
  };
}

function setFilter(filter) {
  return {
    type: 'SET_JOB_FILTER',
    filter,
  };
}

function fetchList(filter) {
  return (dispatch, getState) => {
    dispatch(setFilter(filter));
    const timestamp = new Date();
    const state = getState();
    return request(`/jobs${convertDataToParams({
      page: state.job.jobMeta.page,
      ...baseFilter,
      ...state.job.filter,
      archived: false,
    })}`, {
      method: 'GET',
    })
      .then((result) => {
        dispatch(receiveJobs(result, timestamp));
        return result;
      });
  };
}

function fetchArchivedList(filter) {
  return (dispatch, getState) => {
    dispatch(setFilter(filter));
    const timestamp = new Date();
    const state = getState();
    return request(`/jobs${convertDataToParams({
      page: state.job.jobMeta.page,
      ...baseFilter,
      ...state.job.filter,
      archived: true,
    })}`, {
      method: 'GET',
    })
      .then((result) => {
        dispatch(receiveArchivedJobs(result, timestamp));
        return result;
      });
  };
}

function fetch(id) {
  return dispatch => request(`/jobs/${id}`, {
    method: 'GET',
  })
    .then((result) => {
      dispatch(receiveJob(result.job));
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function create(query) {
  return dispatch => request('/jobs', {
    method: 'POST',
  }, {
    job: query,
  })
    .then((result) => {
      dispatch(receiveJobUpdateSuccessful(result.job));
      dispatch(fetchList());
      dispatch(task.fetchList());
      return result;
    });
}

function duplicate(id) {
  return async (dispatch) => {
    const result = await request(`/jobs/${id}/duplicate`, {
      method: 'POST',
    });
    dispatch(fetchList());
    return result;
  };
}

function update(id, query) {
  return dispatch => request(`/jobs/${id}`, {
    method: 'PUT',
  }, {
    job: query,
  })
    .then((result) => {
      dispatch(receiveJobUpdateSuccessful(result.job));
      return result;
    });
}

function archive(id) {
  return () => request(`/jobs/${id}/archive`, {
    method: 'PUT',
  })
    .catch((error) => { Sentry.captureException(error); });
}

function unarchive(id) {
  return dispatch => request(`/jobs/${id}/unarchive`, {
    method: 'PUT',
  })
    .then((result) => {
      dispatch(fetchArchivedList());
      dispatch(fetchList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function archiveMultiple(ids) {
  return () => request('/jobs/update_multiple', {
    method: 'PUT',
  }, {
    ids,
    action_type: 'archive',
  })
    .catch((error) => { Sentry.captureException(error); });
}

function unarchiveMultiple(ids) {
  return dispatch => request('/jobs/update_multiple', {
    method: 'PUT',
  }, {
    ids,
    action_type: 'unarchive',
  })
    .then((result) => {
      dispatch(fetchArchivedList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

async function printMultiple(query) {
  const result = await request('/jobs/multiple_print', {
    method: 'POST',
  }, {
    job: query,
  });
  return result;
}

async function exportJobs(data) {
  return dispatch => requestBlob('/jobs/export', {
    method: 'POST',
  }, data)
    .then((res) => {
      dispatch(receiveJobExportSuccessful(res));
      Utils.Helper.saveBlob(res, 'job_export.xlsx');
      return res;
    });
}

async function getImportTemplate(data) {
  const result = await requestBlob(`/jobs/import/template${convertDataToParams(data)}`, {
    method: 'GET',
  });
  Utils.Helper.saveBlob(result, 'Import_Myjobs_Jobs.xlsx');
}

function importJobs(data) {
  return dispatch => requestCustomBody('/jobs/import', {
    method: 'POST',
  }, data)
    .then((res) => {
      dispatch(fetchList());
      return res;
    });
}

function submit(id) {
  return dispatch => request(`/jobs/${id}/submit`, {
    method: 'PUT',
  })
    .then((result) => {
      dispatch(fetch(id));
      return result;
    });
}

function submitMultiple(ids) {
  return dispatch => request('/jobs/update_multiple', {
    method: 'PUT',
  }, {
    ids,
    action_type: 'submit',
  })
    .then((result) => {
      dispatch(fetchList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function revoke(id) {
  return dispatch => request(`/jobs/${id}/revoke`, {
    method: 'PUT',
  })
    .then((result) => {
      dispatch(fetch(id));
      return result;
    });
}

function revokeMultiple(ids) {
  return dispatch => request('/jobs/update_multiple', {
    method: 'PUT',
  }, {
    ids,
    action_type: 'revoke',
  })
    .then((result) => {
      dispatch(fetchList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function checkExportTaskCount(data) {
  return dispatch => request('/jobs/export/task_count', {
    method: 'POST',
  }, {
    ...data,
  })
    .then((result) => {
      dispatch(receiveExportTaskCountSuccessful(result.task_count));
    });
}

function resetExportTaskCount() {
  return (dispatch) => {
    dispatch(receiveResetJobExportTaskCount());
  };
}

function cancel(id) {
  return dispatch => request(`/jobs/${id}/cancel`, {
    method: 'PUT',
  })
    .then((result) => {
      dispatch(fetch(id));
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

export default {
  resetImportError,
  setFilter,
  fetchList,
  fetchArchivedList,
  fetch,
  create,
  duplicate,
  update,
  archive,
  unarchive,
  archiveMultiple,
  unarchiveMultiple,
  printMultiple,
  exportJobs,
  getImportTemplate,
  importJobs,
  submit,
  submitMultiple,
  revoke,
  revokeMultiple,
  checkExportTaskCount,
  resetExportTaskCount,
  cancel,
};
